<template>
  <div part="base" class="sp-assign-specialties-modal">
    <sp-modal
      :modal-title="store.config.modalTitle"
      width="44rem"
      :open="store.isOpen"
      :closeable.prop="modalCloseable"
      :persistent.prop="store.isSubmitting"
      @update-open="handleUpdateOpen"
      @close="handleClose"
    >
      <sp-card-content-overlay slot="content-overlay" :visible="store.isSubmitting">
        <sp-progress type="ellipsis" size="x-small"></sp-progress>
      </sp-card-content-overlay>

      <InputStep v-if="store.currentStep === 'input'" key="input" />
      <ConfirmStep v-else key="confirm" />

      <ModalActions slot="actions" />
    </sp-modal>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, watch } from "vue";
import { useExpose } from "#ce/composables/useExpose";
import { toBoolean } from "#ce/utils/props";
import InputStep from "./components/InputStep.ce.vue";
import ConfirmStep from "./components/ConfirmStep.ce.vue";
import ModalActions from "./components/ModalActions.ce.vue";
import { props as appProps } from "./props.js";
import { useAssignSpecialtiesStore } from "./store/assign-specialties";

const emit = defineEmits(["assign-specialties-confirmed"]);

const props = defineProps(appProps);

const store = useAssignSpecialtiesStore();
const { exposeMethods } = useExpose();

// In-flight requests block every dismissal route: closeable hides the X, persistent
// blocks Escape/backdrop. Explicit booleans via `.prop` — an undefined binding would
// stick as false across the CE boundary (see modal-closeable.spec.js).
const modalCloseable = computed(() => !store.isSubmitting);

function buildConfig(overrides = {}) {
  return {
    previewUrl: props.previewUrl,
    processUrl: props.processUrl,
    clientName: props.clientName,
    specialties: props.specialties,
    dropzoneText: props.dropzoneText,
    fileSpecHtml: props.fileSpecHtml,
    termInputLabel: props.termInputLabel,
    modeAddLabel: props.modeAddLabel,
    modeReplaceLabel: props.modeReplaceLabel,
    modeLabel: props.modeLabel,
    specialtySelectLabel: props.specialtySelectLabel,
    flagToggleLabel: props.flagToggleLabel,
    clientControlEnabled: props.clientControlEnabled,
    outcomeLabels: props.outcomeLabels,
    statusLabels: props.statusLabels,
    labels: props.labels,
    modalTitle: props.modalTitle,
    ...overrides,
  };
}

function open(overrides) {
  store.configure(buildConfig(overrides));
  store.open();
}

function close() {
  store.close();
}

exposeMethods({ open, close });

watch(
  () => store.completedCount,
  (value, oldValue) => {
    if (value > (oldValue ?? 0)) {
      emit("assign-specialties-confirmed");
    }
  },
);

onMounted(() => {
  store.configure(buildConfig());

  if (toBoolean(props.autoOpen)) {
    nextTick(() => store.open());
  }
});

function handleUpdateOpen({ detail }) {
  const [value] = detail;

  if (value) {
    // sp-modal re-emits update-open(true) for an open the store initiated;
    // opening again would double-bump openGeneration per open.
    if (!store.isOpen) {
      store.open();
    }
    return;
  }

  handleClose();
}

function handleClose() {
  // Ignore dismissal while the confirmed write is in flight.
  if (store.isSubmitting) {
    return;
  }

  close();
}
</script>

<style>
:host {
  display: block;
}
</style>

<style scoped>
.sp-assign-specialties-modal {
  display: block;

  --sp-comp-card-padding-block: 1rem;
  --sp-card-actions-padding-inline: 0;
  --sp-card-actions-padding-block: 0;
}
</style>
